import React from 'react';
import { UNLOCK_FEEDBACK_QUOTA } from '../../contracts/scanFeedbackApi';

// UnlockProgress — how close the caller is to opening their own sealed reading.
//
// ── WHY A COMPONENT ─────────────────────────────────────────────────────────
//
// Four surfaces show this one number: the unlock modal, the Explore header,
// Home's sealed hero, and the Vault tile. It lived inline in UnlockGateModal,
// so Explore rendered a bare line of text instead and the two could disagree
// about the same count. One definition, four call sites.
//
// ── CELLS BY DEFAULT, A BAR ON EXPLORE ──────────────────────────────────────
//
// DESIGN.md §2.2 bans Aurum as a flood fill on large surfaces, and §16.2 bans
// continuous progress bars on the SCAN SUBMIT surface, where the FaceMeshOverlay
// is the wait state. Ten discrete cells satisfy both while still reading as
// progress, and are more honest than a bar besides — the quantity really is
// countable, and "3 of 10" is a promise a percentage cannot make.
//
//   ▓▓▓ ▓▓▓ ▓▓▓ ░░░ ░░░ ░░░ ░░░ ░░░ ░░░ ░░░     3 / 10     variant="cells"
//    ^filled      ^next (pulses)   ^empty
//
//   ▓▓▓▓▓▓▓▓░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░     3 / 10     variant="bar"
//
// Explore takes the bar. Not a rule change — §16.2 is scoped to the scan
// surface, and Explore is not it — but a collision one: the judging card's own
// ScorePicker is a row of ten boxes, and stacking the reporting row above the
// collecting row made them read as the same control twice.
//
// ── THE CLAMP IS LOAD-BEARING ───────────────────────────────────────────────
//
// `done` is normalized server-side by progressToward() before it ever gets
// here, but the row clamps again anyway. The count arrives from four different
// callables, and the failure this guards against already shipped once: the deck
// and the submit response both returned the RAW lifetime counter, so a user on
// their second scan had done=13 against required=10 — thirteen cells in a row
// of ten. Cheap insurance at the last point where it can still be caught.

export default function UnlockProgress({
  done = 0,
  required = UNLOCK_FEEDBACK_QUOTA,
  // `full` draws the numerals + label beside the row (the modal). Explore and
  // Home want the row alone under their own heading.
  full = false,
  // 'cells' (default) or 'bar'. Same count, same accessible output, different
  // shape — see `bar` below for why Explore wants the continuous one.
  variant = 'cells',
  label,
  className = '',
  'data-testid': testId = 'unlock-progress-row',
}) {
  const total = Math.max(1, Math.floor(Number(required) || UNLOCK_FEEDBACK_QUOTA));
  const filled = Math.min(total, Math.max(0, Math.floor(Number(done) || 0)));

  const pct = Math.round((filled / total) * 100);

  // The continuous form, for Explore. Ten cells read as ten equal steps, which
  // is right beside a big 03 / 10 numeral in the modal but competes with the
  // judging card's own ten-cell ScorePicker directly below it — two rows of ten
  // boxes on one screen, one reporting and one collecting.
  //
  // Allowed here: DESIGN.md §16.2's ban on progress bars is scoped to the scan
  // submit surface, where the FaceMeshOverlay IS the wait state. Explore is not
  // that surface. The fill is a 5px hairline, not a flood — §2.2's Aurum ban is
  // on large surfaces.
  const bar = (
    <div
      className="h-[5px] w-full overflow-hidden rounded-[3px] bg-white/[0.07]"
      aria-hidden="true"
      data-testid="unlock-progress-bar"
    >
      <div
        className="h-full rounded-[3px] bg-aurum-pip shadow-[0_0_9px_rgba(201,164,92,0.45)] transition-[width] duration-500 ease-mogged"
        style={{ width: `${pct}%` }}
        data-testid="unlock-progress-fill"
        data-filled-pct={pct}
      />
    </div>
  );

  const row = (
    <div className="flex gap-[6px]" aria-hidden="true">
      {Array.from({ length: total }, (unused, i) => (
        <div
          key={i}
          data-testid="unlock-pip"
          data-filled={i < filled ? 'true' : 'false'}
          className={[
            'h-[9px] flex-1 rounded-[3px] border',
            i < filled
              ? 'bg-aurum-pip border-aurum/90 shadow-[0_0_9px_rgba(201,164,92,0.5)]'
              : 'bg-white/[0.06] border-white/10',
            i === filled
              ? 'border-aurum/90 motion-safe:animate-[pip-pulse_1.7s_ease-in-out_infinite]'
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      ))}
    </div>
  );

  return (
    <div
      className={className}
      data-testid={testId}
      // The row itself is aria-hidden decoration; the count is the accessible
      // content, so a screen reader hears "3 of 10" rather than ten divs.
      role="progressbar"
      aria-valuenow={filled}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuetext={`${filled} of ${total}`}
      aria-label={label}
    >
      {full && (
        <div className="mb-3 flex items-baseline gap-1" data-testid="unlock-progress">
          <span className="font-censor text-[30px] font-semibold leading-none text-aurum-bright tabular-nums">
            {String(filled).padStart(2, '0')}
          </span>
          <span className="font-censor text-[15px] text-bone-faint tabular-nums">
            {`/ ${total}`}
          </span>
        </div>
      )}
      {variant === 'bar' ? bar : row}
    </div>
  );
}
